// Host da pilha de overlays da ficha (docs/plan/tv-ficha-overlay.md, contrato P0).
//
// Montado como IRMÃO do RouterProvider (fora da árvore roteada) — a origem
// roteada nunca desmonta enquanto há overlay. O host faz DUAS coisas:
//
//   1. É o dono do ÁRBITRO DE BACK: um único listener global em CAPTURE phase
//      que, com a pilha NÃO-vazia, trata o Back (pop do topo) e ENGOLE o evento
//      (o `useBackKey` da origem, que escuta em bubble no window, nunca o vê).
//      Com a pilha VAZIA, ele NÃO TOCA no evento — o Back cai no
//      useBackKey/hierárquico da origem, byte-a-byte como hoje. Zero tecla-morta,
//      zero duplo-Back.
//
//   2. Renderiza a pilha num PORTAL acima de tudo. Em P0 a pilha está sempre
//      vazia (nenhum card chama `openDetail` ainda) → o host é inerte. P1 (VOD) e
//      P2 (Série) preenchem o `renderLayer` com as telas de overlay de verdade.
//
// O árbitro reusa `isBackKeyEvent` (a MESMA matriz de teclas do useBackKey —
// 10009/461/Backspace/Esc/GoBack, sem duplicar) e, no pop de uma ficha, reusa
// `startEntryFocus` (a máquina provada que só foca key REGISTRADA, prova o pouso
// pelo [data-focused] e cai no safeKey se o card sumiu) — NUNCA setFocus cru.

import { lazy, Suspense, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { getStack, useStack, type OverlayLayer } from '../detail-overlay-store'
import { isBackKeyEvent } from '../hooks/useBackKey'
import { closeTopLayer, OverlayLayerIndex } from '../hooks/useDetailOverlay'
import { hasBackInterceptor } from '../lib/overlay-back-guard'

// As telas de overlay são LAZY — o host está sempre montado, mas o código pesado
// da ficha/player só carrega quando uma camada é empilhada. Mesma tela que era
// rota (agora sem useParams: recebe a camada por prop).
const MovieDetailsScreen = lazy(() =>
  import('../screens/MovieDetailsScreen').then((m) => ({ default: m.MovieDetailsScreen })),
)
const VodPlayerScreen = lazy(() =>
  import('../screens/VodPlayerScreen').then((m) => ({ default: m.VodPlayerScreen })),
)
const SeriesDetailsScreen = lazy(() =>
  import('../screens/SeriesDetailsScreen').then((m) => ({ default: m.SeriesDetailsScreen })),
)
const SeriesPlayerScreen = lazy(() =>
  import('../screens/SeriesPlayerScreen').then((m) => ({ default: m.SeriesPlayerScreen })),
)

/**
 * Cobre a tela enquanto o chunk lazy da camada carrega — opaca pra a origem não
 * PISCAR atrás no primeiro open. Inline com top/left/right/bottom (NÃO `inset`, que
 * em style inline não passa pelo postcss e é ignorada no piso antigo) e cor
 * literal (var() inline é sintaxe inválida no Chr38). z-index no nível da ficha.
 */
function OverlayFallback() {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 250,
        background: '#000',
      }}
    />
  )
}

/**
 * Renderiza UMA camada da pilha. P1: filme (ficha + player). Série é P2 (null por
 * ora). Keada por ÍNDICE: a pilha é LIFO pura (push/pop no topo), então o índice é
 * estável pras camadas de baixo — a origem/ficha embaixo NÃO remonta quando o
 * topo entra/sai, e a recomendação (que TROCA a ficha do índice 0) reusa a
 * instância trocando só o `id` (sem flash). Ver docs/plan/tv-ficha-overlay.md.
 */
function layerContent(layer: OverlayLayer): React.ReactNode {
  switch (layer.kind) {
    case 'movie-detail':
      return <MovieDetailsScreen id={layer.id} />
    case 'vod-player':
      return <VodPlayerScreen sourceId={layer.sourceId} resumeAt={layer.resumeAt} />
    // P2 — série: ficha + player + auto-advance. Espelha o filme (mesmo key por
    // índice; a ficha embaixo não remonta quando o player entra/sai, e a
    // recomendação REUSA a instância trocando só o `id`).
    case 'series-detail':
      return <SeriesDetailsScreen id={layer.id} />
    case 'series-player':
      return (
        <SeriesPlayerScreen
          seriesId={layer.seriesId}
          episodeId={layer.episodeId}
          resumeAt={layer.resumeAt}
          // A instância da camada É a sessão (cada push/replace cria uma nova): o
          // estado "deste episódio" do player é chaveado por ela, não pelo id.
          session={layer}
        />
      )
    default:
      return null
  }
}

/**
 * O ÍNDICE vai junto com a camada. É o que dá a ela como saber se é o TOPO —
 * e portanto a dona das teclas de tela (`ownsScreenKeys`). Sem isto, a ficha
 * embaixo do player continua ouvindo a MESMA tecla e o favoritar do amarelo
 * dispara duas vezes (ver a nota longa em hooks/useDetailOverlay.ts).
 *
 * O índice é estável enquanto a camada vive: a pilha é LIFO pura, então empilhar
 * ou desempilhar o topo não mexe no índice de quem está embaixo.
 */
function renderLayer(layer: OverlayLayer, index: number): React.ReactNode {
  return (
    <OverlayLayerIndex.Provider key={index} value={index}>
      <Suspense fallback={<OverlayFallback />}>{layerContent(layer)}</Suspense>
    </OverlayLayerIndex.Provider>
  )
}

export function DetailOverlayHost() {
  const stack = useStack()

  // Cancelador do último restore de foco em curso (startEntryFocus agenda rAF +
  // timers). Cancelamos antes de disparar outro e no unmount.
  const restoreCancelRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Lê a pilha SÍNCRONA (getStack) dentro do keydown — nunca um closure velho.
    const onKey = (e: KeyboardEvent): void => {
      if (!isBackKeyEvent(e)) return
      // Pilha vazia: NÃO mexe no evento. O Back segue pro useBackKey/hierárquico
      // da rota exatamente como antes desta frente existir.
      if (getStack().length === 0) return

      // Modal ANINHADO aberto por cima da camada (TrailerOverlay) quer o Back
      // primeiro. Não engolimos: deixamos o evento seguir pro handler (capture,
      // registrado DEPOIS) do modal, que fecha só a si mesmo. Sem isto o árbitro
      // (registrado no boot, roda antes) fecharia a FICHA inteira em vez do trailer.
      if (hasBackInterceptor()) return

      // Pilha não-vazia: este Back é NOSSO. Capture + stopImmediatePropagation
      // impedem o useBackKey da origem (bubble no window) de rodar no mesmo
      // evento — sem isto o Back fecharia o overlay E navegaria (duplo-Back).
      e.preventDefault()
      e.stopImmediatePropagation()

      // Fecha pela máquina COMPARTILHADA (mesma dos botões "Voltar" das fichas):
      // pop + restore de foco guardado. Ficha removida → foco volta pro card de
      // origem; player removido → a ficha reaparece e refoca o próprio Play
      // (closeTopLayer devolve null). Cancela um restore anterior antes do novo.
      restoreCancelRef.current?.()
      restoreCancelRef.current = closeTopLayer()
    }

    // window E document em capture, como o TrailerOverlay: cobre engines de TV
    // que entregam o keydown num alvo abaixo do window. O stopImmediatePropagation
    // do primeiro a rodar (window, mais alto na captura) já barra o segundo —
    // então o pop acontece UMA vez só.
    window.addEventListener('keydown', onKey, { capture: true })
    document.addEventListener('keydown', onKey, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true })
      document.removeEventListener('keydown', onKey, { capture: true })
      restoreCancelRef.current?.()
      restoreCancelRef.current = null
    }
  }, [])

  // P0: pilha sempre vazia → nada a pintar. O portal (acima de tudo, com o
  // contexto do React preservado — Tier/Brand/i18n dos AppProviders) só entra em
  // cena quando P1/P2 empilharem camadas.
  if (stack.length === 0) return null
  return createPortal(<>{stack.map(renderLayer)}</>, document.body)
}
